import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { BACKEND_URL } from '../config';
import '../css/ReviewsPage.css';

const PAGE_SIZE = 20;

const RATING_LABEL = { 5: 'Відмінно', 4: 'Добре', 3: 'Нормально', 2: 'Погано', 1: 'Дуже погано' };

const ALL_TAGS = [
    'Гарне обслуговування',
    'Ввічливий продавець',
    'Швидко відправили',
    'Швидко зв\'язались',
    'Актуальна ціна',
    'Актуальний опис',
    'Товар був у наявності',
    'Неввічливий продавець',
    'Відправляли довше обіцяного',
    'Зі мною не зв\'язались',
    'Товар не відповідав опису',
    'Товар не відправили',
    'Товару не було в наявності',
    'Ціна вище заявленої',
];

const MONTH_NAMES = ['січня','лютого','березня','квітня','травня','червня',
    'липня','серпня','вересня','жовтня','листопада','грудня'];

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
};

const Stars = ({ score, size = 'md' }) => (
    <span className={`rv-stars rv-stars--${size}`} aria-label={`${score} з 5`}>
        {[1,2,3,4,5].map(i => (
            <span key={i} className={i <= score ? 'rv-star rv-star--on' : 'rv-star'}>★</span>
        ))}
    </span>
);

const ReviewCard = ({ review }) => {
    const tags = review.tags ? review.tags.split(';').map(t => t.trim()).filter(Boolean) : [];
    const initials = (review.author || '?').charAt(0).toUpperCase();
    const label = RATING_LABEL[review.ratingScore] || review.ratingText;

    return (
        <article className="rv-card">
            <div className="rv-card__top">
                <Stars score={review.ratingScore} />
                <span className={`rv-badge rv-badge--${review.ratingScore}`}>{label}</span>
            </div>

            {review.reviewText && (
                <p className="rv-card__text">&ldquo;{review.reviewText}&rdquo;</p>
            )}

            {tags.length > 0 && (
                <div className="rv-card__tags">
                    {tags.map(tag => (
                        <span key={tag} className="rv-card__tag">{tag}</span>
                    ))}
                </div>
            )}

            {review.productName && (
                <p className="rv-card__product">
                    <svg className="rv-card__product-icon" viewBox="0 0 16 16" width="13" height="13" fill="currentColor">
                        <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                    </svg>
                    {review.productName}
                </p>
            )}

            <div className="rv-card__footer">
                <span className="rv-card__avatar">{initials}</span>
                <div className="rv-card__author-info">
                    <span className="rv-card__author">{review.author}</span>
                </div>
                <time className="rv-card__date" dateTime={review.datetimeIso}>
                    {formatDate(review.datetimeIso)}
                </time>
            </div>
        </article>
    );
};

const RatingSummary = ({ totalItems, reviews }) => {
    if (!reviews.length) return null;
    const avg = (reviews.reduce((s, r) => s + (r.ratingScore || 0), 0) / reviews.length).toFixed(1);
    const dist = [5,4,3,2,1].map(score => ({
        score,
        count: reviews.filter(r => r.ratingScore === score).length,
    }));
    const max = Math.max(...dist.map(d => d.count), 1);

    return (
        <div className="rv-summary">
            <div className="rv-summary__score">
                <span className="rv-summary__avg">{avg}</span>
                <Stars score={Math.round(parseFloat(avg))} size="lg" />
                <span className="rv-summary__total">{totalItems} відгуків</span>
            </div>
            <div className="rv-summary__bars">
                {dist.map(({ score, count }) => (
                    <div key={score} className="rv-bar-row">
                        <span className="rv-bar-row__label">{score} ★</span>
                        <div className="rv-bar-row__track">
                            <div
                                className="rv-bar-row__fill"
                                style={{ width: `${(count / max) * 100}%` }}
                            />
                        </div>
                        <span className="rv-bar-row__count">{count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const EMPTY_FORM = { author: '', email: '', ratingScore: 0, reviewText: '', tags: [], productName: '' };

const AddReviewModal = ({ onClose }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const toggleTag = (tag) => setForm(f => ({
        ...f,
        tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag],
    }));

    const validate = () => {
        if (!form.author.trim()) return 'Вкажіть ваше ім\'я';
        if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Вкажіть коректний e-mail';
        if (!form.ratingScore) return 'Оберіть оцінку';
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) { setError(err); return; }
        setError('');
        setSubmitting(true);
        try {
            const res = await fetch(`${BACKEND_URL}/reviews`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    author: form.author.trim(),
                    ratingScore: form.ratingScore,
                    ratingText: RATING_LABEL[form.ratingScore] || '',
                    reviewText: form.reviewText.trim() || null,
                    tags: form.tags.join('; ') || null,
                    productName: form.productName.trim() || null,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setSubmitted(true);
        } catch {
            setError('Щось пішло не так. Спробуйте ще раз.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="rv-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="rv-modal">
                <button className="rv-modal__close" onClick={onClose} aria-label="Закрити">✕</button>
                <div style={{ clear: 'both' }} />

                {submitted ? (
                    <div className="rv-modal__success">
                        <div className="rv-modal__success-icon">✓</div>
                        <h3>Дякуємо за ваш відгук!</h3>
                        <p>
                            Ваш відгук успішно отримано та буде опублікований після проходження
                            модерації. Зазвичай це займає не більше 24&nbsp;годин. Ми цінуємо вашу
                            думку та докладаємо зусиль, щоб кожен відгук був розглянутий
                            уважно та своєчасно.
                        </p>
                        <button className="rv-modal__done-btn" onClick={onClose}>Закрити</button>
                    </div>
                ) : (
                    <form className="rv-modal__form" onSubmit={handleSubmit} noValidate>
                        <h2 className="rv-modal__title">Залишити відгук</h2>

                        <div className="rv-form-group">
                            <label className="rv-form-label">Оцінка <span className="rv-required">*</span></label>
                            <div className="rv-star-picker">
                                {[1,2,3,4,5].map(s => (
                                    <button
                                        key={s}
                                        type="button"
                                        className={`rv-star-pick${form.ratingScore >= s ? ' rv-star-pick--on' : ''}`}
                                        onClick={() => setField('ratingScore', s)}
                                        aria-label={`${s} зірок`}
                                    >★</button>
                                ))}
                                {form.ratingScore > 0 && (
                                    <span className={`rv-badge rv-badge--${form.ratingScore}`} style={{ marginLeft: 10 }}>
                                        {RATING_LABEL[form.ratingScore]}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="rv-form-row">
                            <div className="rv-form-group">
                                <label className="rv-form-label" htmlFor="rv-author">
                                    Ім'я <span className="rv-required">*</span>
                                </label>
                                <input
                                    id="rv-author"
                                    className="rv-form-input"
                                    type="text"
                                    placeholder="Іван П."
                                    value={form.author}
                                    onChange={e => setField('author', e.target.value)}
                                    maxLength={80}
                                />
                            </div>
                            <div className="rv-form-group">
                                <label className="rv-form-label" htmlFor="rv-email">
                                    E-mail <span className="rv-required">*</span>
                                </label>
                                <input
                                    id="rv-email"
                                    className="rv-form-input"
                                    type="email"
                                    placeholder="your@email.com"
                                    value={form.email}
                                    onChange={e => setField('email', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="rv-form-group">
                            <label className="rv-form-label" htmlFor="rv-text">Коментар</label>
                            <textarea
                                id="rv-text"
                                className="rv-form-input rv-form-textarea"
                                placeholder="Розкажіть про ваш досвід..."
                                value={form.reviewText}
                                onChange={e => setField('reviewText', e.target.value)}
                                rows={3}
                                maxLength={1000}
                            />
                        </div>

                        <div className="rv-form-group">
                            <label className="rv-form-label">Теги</label>
                            <div className="rv-tag-picker">
                                {ALL_TAGS.map(tag => (
                                    <button
                                        key={tag}
                                        type="button"
                                        className={`rv-tag-pick${form.tags.includes(tag) ? ' rv-tag-pick--on' : ''}`}
                                        onClick={() => toggleTag(tag)}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="rv-form-group">
                            <label className="rv-form-label" htmlFor="rv-product">Назва товару</label>
                            <input
                                id="rv-product"
                                className="rv-form-input"
                                type="text"
                                placeholder="напр. BMW E46 Насос ГУР"
                                value={form.productName}
                                onChange={e => setField('productName', e.target.value)}
                                maxLength={200}
                            />
                        </div>

                        {error && <p className="rv-form-error">{error}</p>}

                        <button className="rv-modal__submit" type="submit" disabled={submitting}>
                            {submitting ? 'Надсилання...' : 'Надіслати відгук'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

const ReviewsPage = () => {
    const [showModal, setShowModal] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [allReviews, setAllReviews] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchReviews = useCallback(async (page) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${BACKEND_URL}/reviews?page=${page}&size=${PAGE_SIZE}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setReviews(data.reviews || []);
            setCurrentPage(data.currentPage ?? page);
            setTotalPages(data.totalPages ?? 0);
            setTotalItems(data.totalItems ?? 0);
        } catch {
            setError('Не вдалося завантажити відгуки. Спробуйте пізніше.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Окремий запит для статистики — всі відгуки одразу
    const fetchAllForStats = useCallback(async () => {
        try {
            const first = await fetch(`${BACKEND_URL}/reviews?page=0&size=1`);
            if (!first.ok) return;
            const { totalItems: total } = await first.json();
            if (!total) return;
            const res = await fetch(`${BACKEND_URL}/reviews?page=0&size=${total}`);
            if (!res.ok) return;
            const data = await res.json();
            setAllReviews(data.reviews || []);
        } catch {
            // статистика не критична — мовчки ігноруємо
        }
    }, []);

    useEffect(() => {
        fetchReviews(0);
        fetchAllForStats();
    }, [fetchReviews, fetchAllForStats]);

    const handlePageChange = (page) => {
        if (page < 0 || page >= totalPages) return;
        fetchReviews(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const pages = [];
        const left = Math.max(0, currentPage - 2);
        const right = Math.min(totalPages - 1, currentPage + 2);
        if (left > 0) { pages.push(0); if (left > 1) pages.push('...'); }
        for (let i = left; i <= right; i++) pages.push(i);
        if (right < totalPages - 1) { if (right < totalPages - 2) pages.push('...'); pages.push(totalPages - 1); }

        return (
            <div className="rv-pagination">
                <button className="rv-pagination__btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>←</button>
                {pages.map((p, i) => p === '...'
                    ? <span key={`d${i}`} className="rv-pagination__dots">…</span>
                    : <button key={p} className={`rv-pagination__btn${p === currentPage ? ' rv-pagination__btn--active' : ''}`} onClick={() => handlePageChange(p)}>{p + 1}</button>
                )}
                <button className="rv-pagination__btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages - 1}>→</button>
            </div>
        );
    };

    return (
        <Layout>
            <SEO
                title="Відгуки покупців — Union Auto"
                description="Реальні відгуки покупців про запчастини та обслуговування інтернет-магазину Union Auto."
                canonical="/reviews"
            />
            {showModal && <AddReviewModal onClose={() => setShowModal(false)} />}

            <div className="rv-page">
                <div className="rv-page__header">
                    <div className="rv-page__header-row">
                        <div>
                            <h1 className="rv-page__title">Відгуки покупців</h1>
                            <p className="rv-page__subtitle">Що кажуть клієнти про наші запчастини та сервіс</p>
                        </div>
                        <button className="rv-add-btn" onClick={() => setShowModal(true)}>
                            + Залишити відгук
                        </button>
                    </div>
                </div>

                {allReviews.length > 0 && (
                    <RatingSummary totalItems={totalItems} reviews={allReviews} />
                )}

                {loading && (
                    <div className="rv-page__loading">
                        <div className="rv-spinner" />
                        <span>Завантаження відгуків...</span>
                    </div>
                )}

                {error && !loading && (
                    <div className="rv-page__error">
                        <p>{error}</p>
                        <button className="rv-page__retry" onClick={() => fetchReviews(currentPage)}>Спробувати знову</button>
                    </div>
                )}

                {!loading && !error && reviews.length === 0 && (
                    <p className="rv-page__empty">Відгуків поки немає.</p>
                )}

                {!loading && !error && reviews.length > 0 && (
                    <>
                        <div className="rv-list">
                            {reviews.map(r => <ReviewCard key={r.id} review={r} />)}
                        </div>
                        {renderPagination()}
                    </>
                )}
            </div>
        </Layout>
    );
};

export default ReviewsPage;
